import { Stack } from '@mui/material'
import { EyeIcon } from '@phosphor-icons/react'
import {
    createAccountPresentation,
    createExternalParticipantPresentation,
    tokens,
    User,
    UserBubbleList,
    type ExternalParticipantDisplayData,
} from 'ComponentLibrary'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { BooleanButton } from 'ComponentLibrary/src/piazza/buttons/BooleanButton/BooleanButton'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type HeaderObserversProps = {
    currentUser?: User
    setIsEditingObservers: (isEditingObservers: boolean) => void
    observerUUIDs: UUID[]
    users: User[]
    externalParticipantUsers?: ExternalParticipantDisplayData[]
    disabled?: boolean
    replaceObserverUUIDs: (observerUUIDs: UUID[]) => void
    disableCurrentUserToggle?: boolean
    copy?: Partial<HeaderObserversCopy>
    userLinkCopy?: Partial<UserLinkCopy>
}

export type HeaderObserversCopy = {
    protectedTooltip: string
    platformUserTooltip: string
    observingTooltip: string
    notObservingTooltip: string
    addObserverTooltip: string
    observerGroup: string
    disableObservationAriaLabel: string
    enableObservationAriaLabel: string
}

const DEFAULT_HEADER_OBSERVERS_COPY: HeaderObserversCopy = {
    protectedTooltip: 'Als Zuständige/r dieses Auftrags beobachten Sie ihn automatisch.',
    platformUserTooltip: 'Sie können sich als Plattformnutzer nicht als Beobachter hinzufügen.',
    observingTooltip: 'Sie beobachten diesen Auftrag. Klicken, um nicht mehr zu beobachten.',
    notObservingTooltip: 'Sie beobachten diesen Auftrag nicht. Klicken, um zu beobachten.',
    addObserverTooltip: 'Beobachter hinzufügen',
    observerGroup: 'Beobachter',
    disableObservationAriaLabel: 'Beobachtung deaktivieren',
    enableObservationAriaLabel: 'Beobachtung aktivieren',
}

export const HeaderObservers = ({
    currentUser,
    replaceObserverUUIDs,
    setIsEditingObservers,
    observerUUIDs,
    users,
    externalParticipantUsers = [],
    disabled,
    disableCurrentUserToggle,
    copy,
    userLinkCopy,
}: HeaderObserversProps) => {
    const resolvedCopy = { ...DEFAULT_HEADER_OBSERVERS_COPY, ...copy }
    const observerUsers = users.filter((user) => observerUUIDs.includes(user.uuid))
    const observerPresentations = observerUsers.map((user) => createAccountPresentation(user))
    const externalPresentations = externalParticipantUsers.map(createExternalParticipantPresentation)

    const addCurrentUserToObservers = () => currentUser && replaceObserverUUIDs?.([...observerUUIDs, currentUser.uuid])

    const removeCurrentUserFromObservers = () =>
        currentUser && replaceObserverUUIDs?.(observerUUIDs.filter((uuid) => uuid !== currentUser.uuid))

    const isNotifiedForCurrentUser = !!currentUser && observerUUIDs.includes(currentUser.uuid)
    const isPlatformUser = !!currentUser?.is_platform_user
    const isAddSelfDisabled = isPlatformUser && !isNotifiedForCurrentUser
    const isToggleDisabled = disabled || (!!disableCurrentUserToggle && isNotifiedForCurrentUser) || isAddSelfDisabled

    const handleToggleClick = () => {
        if (isToggleDisabled) {
            return
        }

        if (isNotifiedForCurrentUser) {
            removeCurrentUserFromObservers?.()
        } else {
            addCurrentUserToObservers?.()
        }
    }

    const getTooltipText = () => {
        if (disableCurrentUserToggle && isNotifiedForCurrentUser) {
            return resolvedCopy.protectedTooltip
        }
        if (isAddSelfDisabled) {
            return resolvedCopy.platformUserTooltip
        }
        if (isNotifiedForCurrentUser) {
            return resolvedCopy.observingTooltip
        }
        return resolvedCopy.notObservingTooltip
    }

    const tooltipText = getTooltipText()

    return (
        <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
            <UserBubbleList
                tooltip={resolvedCopy.addObserverTooltip}
                userLinkCopy={userLinkCopy}
                presentationGroups={[
                    {
                        groupName: resolvedCopy.observerGroup,
                        presentations: [...observerPresentations, ...externalPresentations],
                    },
                ]}
                onClick={() => setIsEditingObservers(true)}
            />
            <BooleanButton
                size="medium"
                selected={isNotifiedForCurrentUser}
                disabled={isToggleDisabled}
                onChange={handleToggleClick}
                icon={<EyeIcon />}
                tooltip={tooltipText}
                ariaLabel={isNotifiedForCurrentUser ? resolvedCopy.disableObservationAriaLabel : resolvedCopy.enableObservationAriaLabel}
            />
        </Stack>
    )
}
